import { useEffect, useRef, useState } from "react";
import type { Browser } from "igv";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import {
  artifactDownloadPath,
  artifactMetadataPath,
  type ArtifactDownloadResult,
  type ArtifactMetadata,
  type ArtifactPreview,
} from "~/services/artifacts";
import { apiClient, apiMutate } from "~/utils/apiClient";
import { ArtifactShell } from "./ArtifactShell";
import { FileArtifactBody } from "./FileArtifactBody";

const GENOMICS_EXTENSIONS = new Set(["bam", "cram"]);
const MAX_GENOMICS_FILE_BYTES = 500 * 1024 * 1024;

export function isGenomicsAlignmentArtifact(preview: ArtifactPreview): boolean {
  const filename = filenameFromPreview(preview);
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return GENOMICS_EXTENSIONS.has(ext);
}

function filenameFromPreview(preview: ArtifactPreview): string {
  const c = preview.content;
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const fileName = (c as { file_name?: unknown }).file_name;
    if (typeof fileName === "string" && fileName) {
      return fileName;
    }
  }
  return preview.filename ?? preview.artifact_name ?? "";
}

interface GenomicsViewerProps {
  preview: ArtifactPreview;
  className?: string;
}

const GenomicsViewer = ({ preview, className }: GenomicsViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const browserRef = useRef<Browser | null>(null);
  const igvRef = useRef<{ removeBrowser: (b: Browser) => void } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { conversationId } = useConversationContext();
  const filename = filenameFromPreview(preview);

  useEffect(() => {
    if (!containerRef.current || !conversationId || !preview.artifact_id) return;

    let cancelled = false;

    const loadViewer = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const content = preview.content as Record<string, unknown> | null;
        let indexArtifactId = content?.index_artifact_id as string | undefined;
        let referenceGenome = content?.reference_genome as string | undefined;

        let fileSizeBytes = content?.file_size_bytes as number | undefined;

        if (!indexArtifactId) {
          try {
            const metadata = await apiClient<ArtifactMetadata>(
              artifactMetadataPath(conversationId, preview.artifact_id)
            );
            indexArtifactId = metadata.custom_metadata?.index_artifact_id as string | undefined;
            referenceGenome =
              referenceGenome ?? (metadata.custom_metadata?.reference_genome as string | undefined);
            fileSizeBytes =
              fileSizeBytes ?? (metadata.custom_metadata?.file_size_bytes as number | undefined);
          } catch {
            // metadata endpoint unavailable — fall through to error below
          }
        }

        if (!indexArtifactId) {
          throw new Error(
            "No companion index file found. BAM/CRAM files require a .bai/.crai index."
          );
        }

        if (fileSizeBytes && fileSizeBytes > MAX_GENOMICS_FILE_BYTES) {
          throw new Error("File is too large to render inline. Please download it instead.");
        }

        const [downloadResult, indexDownloadResult] = await Promise.all([
          apiMutate<ArtifactDownloadResult>(
            artifactDownloadPath(conversationId, preview.artifact_id),
            "POST",
            { format: "original" }
          ),
          apiMutate<ArtifactDownloadResult>(
            artifactDownloadPath(conversationId, indexArtifactId),
            "POST",
            { format: "original" }
          ),
        ]);
        if (cancelled || !containerRef.current) return;

        const { default: igv } = await import("igv");
        igvRef.current = igv;

        if (cancelled || !containerRef.current) return;

        if (browserRef.current) {
          igv.removeBrowser(browserRef.current);
          browserRef.current = null;
        }

        const browser = await igv.createBrowser(containerRef.current, {
          genome: referenceGenome ?? "hg38",
          tracks: [
            {
              type: "alignment",
              format: filename.toLowerCase().endsWith(".cram") ? "cram" : "bam",
              url: downloadResult.download_url,
              indexURL: indexDownloadResult.download_url,
              name: filename,
            },
          ],
        });

        if (cancelled) {
          igv.removeBrowser(browser);
          return;
        }

        browserRef.current = browser;
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load IGV viewer:", err);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadViewer();

    return () => {
      cancelled = true;
      if (browserRef.current && igvRef.current) {
        igvRef.current.removeBrowser(browserRef.current);
        browserRef.current = null;
      }
    };
    // preview.content is intentionally omitted — it doesn't change after the
    // initial render in the current flow (metadata is attached at ingest time).
  }, [conversationId, preview.artifact_id, filename]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 rounded-md border border-border bg-muted/40 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className ?? "min-h-[500px]")}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
          <div className="text-center">
            <Loader2 className="size-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading alignment viewer...</p>
          </div>
        </div>
      )}
      <div ref={containerRef} className={cn("w-full rounded-md", isLoading && "invisible")} />
    </div>
  );
};

export const GenomicsAlignmentArtifact = ({ preview }: { preview: ArtifactPreview }) => (
  <ArtifactShell
    preview={preview}
    expandedContent={<GenomicsViewer preview={preview} className="h-full" />}
  >
    <FileArtifactBody preview={preview} />
    <div className="mt-3">
      <GenomicsViewer preview={preview} />
    </div>
  </ArtifactShell>
);
